import { useEffect, useState } from "react";
import { Download, X, Share, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";

const KEY = "ntv_install_dismissed";

// Encourages mobile users to add NeTimeTV to their home screen (PWA install).
// Android/Chrome: uses the real beforeinstallprompt. iOS Safari: shows manual steps.
export const InstallPrompt = () => {
  const { t } = useTranslation();
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [ios, setIos] = useState(false);

  useEffect(() => {
    if (localStorage.getItem(KEY) === "1") return;
    const standalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;
    const isMobile = window.matchMedia("(max-width: 768px)").matches;
    if (standalone || !isMobile) return;

    const ua = window.navigator.userAgent.toLowerCase();
    const isIOS = /iphone|ipad|ipod/.test(ua);

    const onBIP = (e) => { e.preventDefault(); setDeferred(e); };
    window.addEventListener("beforeinstallprompt", onBIP);

    let timer;
    if (isIOS) timer = setTimeout(() => { setIos(true); setShow(true); }, 4000);

    return () => { window.removeEventListener("beforeinstallprompt", onBIP); if (timer) clearTimeout(timer); };
  }, []);

  useEffect(() => {
    if (!deferred) return;
    const id = setTimeout(() => setShow(true), 1500);
    return () => clearTimeout(id);
  }, [deferred]);

  const close = () => { localStorage.setItem(KEY, "1"); setShow(false); };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try { await deferred.userChoice; } catch (e) { /* ignore */ }
    setDeferred(null);
    close();
  };

  if (!show) return null;

  return (
    <div data-testid="install-prompt" onClick={close}
      className="fixed inset-0 z-[65] flex items-end sm:items-center justify-center sm:p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
      <div onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-sm bg-[#121216] border-t sm:border border-white/10 rounded-t-2xl sm:rounded-2xl p-6 animate-fade-up">
        <div className="flex items-start gap-4">
          <img src="/icon-192.png" alt="NeTimeTV" className="w-14 h-14 rounded-xl shrink-0 ring-1 ring-white/10" />
          <div className="flex-1 min-w-0">
            <h3 className="font-head font-extrabold text-lg leading-tight">{t("install.title")}</h3>
            <p className="text-zinc-400 text-sm mt-1">{ios ? t("install.iosBody") : t("install.body")}</p>
          </div>
          <button data-testid="install-close" onClick={close} aria-label="Close"
            className="shrink-0 w-8 h-8 grid place-items-center rounded-full hover:bg-white/10 transition-colors"><X size={18} /></button>
        </div>
        {ios ? (
          <>
            <div className="mt-4 flex items-center gap-2 text-sm text-zinc-200 bg-white/5 rounded-xl px-4 py-3">
              <Share size={18} className="text-brand shrink-0" />
              <span className="flex-1">{t("install.iosStep")}</span>
              <Plus size={16} className="text-brand shrink-0" />
            </div>
            <button data-testid="install-gotit" onClick={close}
              className="mt-3 w-full bg-white/10 hover:bg-white/15 text-white font-semibold py-2.5 rounded-xl transition-colors">
              {t("install.gotIt")}
            </button>
          </>
        ) : (
          <button data-testid="install-cta" onClick={install}
            className="mt-4 w-full flex items-center justify-center gap-2 bg-brand hover:bg-red-600 text-white font-bold py-3 rounded-xl transition-colors shadow-lg shadow-brand/30">
            <Download size={18} /> {t("install.cta")}
          </button>
        )}
      </div>
    </div>
  );
};
